import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Icon,
  Input,
  Modal,
  NumberInput,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Text,
  useToast,
} from "@app/ui";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import { createAccessGrant, listAccessGrants, revokeAccessGrant } from "../api/dpp.js";
import { useT, useFormatDateTime } from "../i18n.js";
import type { AccessGrant, AccessGrantRole, AccessGrantStatus } from "../types.js";

/**
 * "Restricted access" section of the passport detail page (EN 18239): grants
 * for legitimate-interest parties (repairers, recyclers, customs, partners),
 * create-grant modal, one-time share-link display and revoke. Rendered only
 * for holders of `access:manage` (admin) — mirrors SupplierInvitesCard.
 *
 * The raw access token exists ONLY in the create response — the API stores
 * just its hash. Share URLs are therefore kept in component memory for the
 * session: the copy button works right after creation; older grants honestly
 * say the link cannot be re-shown.
 */

const STATUS_COLOR: Record<AccessGrantStatus, "default" | "success" | "error"> = {
  active: "success",
  revoked: "default",
  expired: "error",
};

const ROLE_COLOR: Record<AccessGrantRole, "primary" | "success" | "warning" | "default"> = {
  repairer: "primary",
  recycler: "success",
  customs: "warning",
  partner: "default",
  authority: "warning",
  refurbisher: "primary",
  distributor: "default",
};

const ROLES: AccessGrantRole[] = [
  "repairer",
  "recycler",
  "customs",
  "partner",
  "authority",
  "refurbisher",
  "distributor",
];

export interface AccessGrantsCardProps {
  orgId: string;
  passportId: string;
}

export function AccessGrantsCard({ orgId, passportId }: AccessGrantsCardProps) {
  const t = useT();
  const fmtDate = useFormatDateTime();
  const toast = useToast();
  const errorText = useErrorText();

  const [grants, setGrants] = useState<AccessGrant[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  /** grantId → full share URL; session-only (tokens are shown once). */
  const [shareUrls, setShareUrls] = useState<Record<string, string>>({});

  // Create-grant modal
  const [createOpen, setCreateOpen] = useState(false);
  const [role, setRole] = useState<AccessGrantRole>("repairer");
  const [holderName, setHolderName] = useState("");
  const [expiresDays, setExpiresDays] = useState("90");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<unknown>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      const page = await listAccessGrants(orgId, passportId, { limit: 100 });
      setGrants(page.items);
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    void load();
  }, [orgId, passportId]);

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("access.linkCopied"));
    } catch {
      toast.error(t("error.actionFailed"));
    }
  };

  const openCreate = () => {
    setRole("repairer");
    setHolderName("");
    setExpiresDays("90");
    setCreateError(null);
    setCreatedUrl(null);
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    if (holderName.trim() === "") {
      setCreateError(t("access.holderRequired"));
      return;
    }
    setCreateError(null);
    setCreating(true);
    try {
      const days = Number(expiresDays);
      const result = await createAccessGrant(orgId, passportId, {
        role,
        holderName: holderName.trim(),
        ...(Number.isFinite(days) && days > 0 ? { expiresInDays: days } : {}),
      });
      // The token is only ever available HERE — build the share URL now.
      const url = `${window.location.origin}${result.sharePath}`;
      setShareUrls((prev) => ({ ...prev, [result.grant.id]: url }));
      setCreatedUrl(url);
      toast.success(t("access.created"));
      await load();
    } catch (e) {
      setCreateError(e);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (grantId: string) => {
    try {
      await revokeAccessGrant(orgId, passportId, grantId);
      toast.success(t("access.revoked"));
      await load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const revocable = (s: AccessGrantStatus) => s === "active" || s === "expired";

  return (
    <Card
      title={t("access.title")}
      extra={
        <Button size="sm" icon={<Icon name="plus" size={14} />} onClick={openCreate}>
          {t("access.grant")}
        </Button>
      }
    >
      {error != null && <ErrorAlert error={error} />}
      {grants === null ? (
        <Spin />
      ) : (
        <Table
          rowKey={(g) => g.id}
          empty={t("access.empty")}
          columns={[
            {
              key: "role",
              title: t("access.role"),
              render: (g: AccessGrant) => <Tag color={ROLE_COLOR[g.role]}>{t(`accessRole.${g.role}`)}</Tag>,
            },
            {
              key: "holder",
              title: t("access.holder"),
              render: (g: AccessGrant) => g.holderName,
            },
            {
              key: "status",
              title: t("common.status"),
              render: (g: AccessGrant) => (
                <Tag color={STATUS_COLOR[g.status]}>{t(`accessStatus.${g.status}`)}</Tag>
              ),
            },
            {
              key: "expiresAt",
              title: t("access.expiry"),
              render: (g: AccessGrant) => fmtDate(g.expiresAt),
            },
            {
              key: "lastAccessedAt",
              title: t("access.lastAccessed"),
              render: (g: AccessGrant) => (g.lastAccessedAt ? fmtDate(g.lastAccessedAt) : "—"),
            },
            {
              key: "actions",
              title: t("common.actions"),
              render: (g: AccessGrant) => (
                <Space size="sm">
                  {shareUrls[g.id] && g.status === "active" && (
                    <Button
                      size="sm"
                      variant="subtle"
                      icon={<Icon name="copy" size={14} />}
                      onClick={() => void copy(shareUrls[g.id]!)}
                    >
                      {t("access.copyLink")}
                    </Button>
                  )}
                  {revocable(g.status) && (
                    <Popconfirm
                      title={t("access.revokeConfirm")}
                      description={t("access.revokeDescription")}
                      okType="danger"
                      okText={t("access.revoke")}
                      cancelText={t("common.cancel")}
                      onConfirm={() => void revoke(g.id)}
                    >
                      <Button size="sm" variant="danger">
                        {t("access.revoke")}
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]}
          data={grants}
        />
      )}

      {/* Create grant */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={createdUrl ? t("access.linkTitle") : t("access.grantTitle")}
        footer={createdUrl ? null : undefined}
        okText={t("common.create")}
        cancelText={t("common.cancel")}
        onOk={() => void submitCreate()}
        confirmLoading={creating}
      >
        {createdUrl ? (
          <Space direction="vertical" size="md" style={{ width: "100%" }}>
            <Alert type="warning">{t("access.linkOnce")}</Alert>
            <Input readOnly value={createdUrl} onFocus={(e) => e.target.select()} />
            <Space size="sm">
              <Button
                variant="primary"
                icon={<Icon name="copy" size={14} />}
                onClick={() => void copy(createdUrl)}
              >
                {t("access.copyLink")}
              </Button>
              <Button onClick={() => setCreateOpen(false)}>{t("common.cancel")}</Button>
            </Space>
          </Space>
        ) : (
          <Space direction="vertical" size="md" style={{ width: "100%" }}>
            {createError != null && <ErrorAlert error={createError} />}
            <Text small secondary>
              {t("access.intro")}
            </Text>
            <Select
              label={t("access.role")}
              value={role}
              onChange={(v) => setRole(v as AccessGrantRole)}
              options={ROLES.map((r) => ({ value: r, label: t(`accessRole.${r}`) }))}
            />
            <Input
              label={t("access.holder")}
              help={t("access.holderHelp")}
              required
              value={holderName}
              onChange={(e) => setHolderName(e.target.value)}
            />
            <NumberInput
              label={t("access.expiresInDays")}
              help={t("access.expiresHelp")}
              min={1}
              max={365}
              decimals={0}
              value={expiresDays}
              onChange={setExpiresDays}
            />
          </Space>
        )}
      </Modal>
    </Card>
  );
}
